import Gigya, { StringList } from './gigya.js'
import BaseParams from './interfaces/base-params.js'

export * from './interfaces/gigya-response.js'
export * from './interfaces/base-params.js'

export interface GroupsDeleteSchemaFieldParams {
	/** The name of the model you want to edit. */
	model: string
	/** This is the field name of the data schema for the group. */
	groupDataSchemaFieldName?: string
	/** This is the relationship field name of the data schema. */
	relationshipDataSchemaFieldName?: string
}

export interface GroupsGetSchemaParams {
	/** The model name to return the schema for. */
	model: string
}

export interface SchemaAttributeDefinition {
	type:
		| 'integer'
		| 'long'
		| 'float'
		| 'string'
		| 'basic-string'
		| 'encrypted-string'
		| 'text'
		| 'date'
		| 'boolean'
	/** validation regex */
	format?: string
	required: boolean
	writeAccess: 'serverOnly' | 'clientCreate' | 'clientModify'
}

export type DataSchema = { [key: string]: SchemaAttributeDefinition }

export interface SchemaType<G = DataSchema, R = DataSchema> {
	groupDataSchema: G
	relationShipDataSchema: R
}

export interface GroupsGetSchemaResponse<G = SchemaType, R = SchemaType> {
	/** The name of the model being returned. */
	model: string
	/** The JSON comprising the schema of the requested model. */
	schema: {
		groupDataSchema: G
		relationshipDataSchema: R
	}
}

export interface GroupsSetSchemaParams {
	/** The name of the model. */
	model: string
	/** optional if relationshipDataSchema provided */
	groupDataSchema?: DataSchema
	/** optional if groupDataSchema provided */
	relationshipDataSchema?: DataSchema
}

export interface GroupsDeleteGroupParams {
	/** The name of the model the group adheres to. */
	model: string
	/** The groupId of the group to delete. */
	groupId: string
}

export interface GroupsGetGroupInfoParams {
	/** The model this group is based upon. */
	model: string
	/** The groupId of the group to return. */
	groupId: string
}

export interface GroupsGetGroupInfoResponse<GroupInfoType> {
	group: GroupInfoType
}

export interface GroupsRegisterGroupParams<GroupDataType> {
	/** Which Model is this group based upon. */
	model: string
	/** The unique groupId for this group. */
	groupId: string
	/** The JSON data for the group as per the group data schema. */
	groupData: GroupDataType
}

export interface GroupsSetGroupInfoParams<GroupDataType> {
	/** The Model this group is based upon. */
	model: string
	/** The unique groupID of the group to update. */
	groupId: string
	/** The JSON data for the group as per the group data schema. */
	groupData: GroupDataType
}

type GroupMemberPermission =
	| 'groupRead'
	| 'groupWrite'
	| 'groupDelete'
	| 'relationshipRead'
	| 'relationshipWrite'
	| 'membersInvite'
	| 'membersAdd'
	| 'membersRemove'
	| 'setPermissions'

export interface GroupsAssignGroupMemberParams<RelationshipDataType> {
	/** The Model the defined group is based upon. */
	model: string
	/**    The unique groupId of the group you are adding the user to. */
	groupId: string
	/** The SAP Customer Data Cloud UID of the user you are adding to the defined group. */
	UID: string
	/** The information for the user based upon the relationship data schema. */
	relationshipData?: RelationshipDataType
	permissions?: GroupMemberPermission[] | string
}

export interface GroupsRemoveMemberParams {
	/** The model the defined group is based upon. */
	model: string
	/** The groupId of the group the user is being removed from. */
	groupId: string
	/** The SAP Customer Data Cloud UID of the user to remove from the defined group. */
	UID: string
}

export interface GroupsSetGroupMemberInfoParams<RelationshipDataType> {
	/** The model the defined group is based upon. */
	model: string
	/**    The unique groupId of the group within the model where the user to modify exists. */
	groupId: string
	/** The SAP Customer Data Cloud UID of the user you are modifying. */
	UID: string
	/** The user's relationship data based on the relationship data schema for the defined group. */
	relationshipData: RelationshipDataType
	permissions?: GroupMemberPermission[] | string
}

export interface GroupsGetSiteConfigResponse {
	/** The maximum number of groups any unique user can be a member of. */
	maxGroupsPerUser: number
}

export type QueryType = StringList<
	| 'select * from relationship'
	| 'select * from groups'
	| 'select count(*) from relationship'
	| 'select count(*) from groups'
>

export interface GroupsSearchParams {
	/** The model you are querying. */
	model: string
	/** The query. You can search from either the groups or relationship objects. You can not pass query and cursorId in the same request. */
	query?: QueryType
	openCursor?: boolean
	cursorId?: string
}

export interface GroupsSearchRelationshipResult<R = any> {
	UID: string
	groupId: string
	model: string
	relationshipData: R
	profile: {
		lastName: string
		email: string
		firstName: string
	}
	// ISO 8601 date
	memberSince: string
	// ISO 8601 date
	lastUpdated: string
	// unix timestamp
	memberSinceTimestamp: number
	// unix timestamp
	lastUpdatedTimestamp: number
}

export interface GroupsSearchGroupsResult<G = any> {
	lastUpdated: string
	lastUpdatedTimestamp: number
	membersCount: number
	created: string
	groupId: string
	createdTimestamp: number
	model: string
	groupData: G
}

export interface GroupsSearchCountResult {
	// one of them is present depending on which variant was used as 'query'
	'COUNT(*)'?: number
	'count(*)'?: number
}

export type GroupsSearchResultType =
	| GroupsSearchGroupsResult
	| GroupsSearchRelationshipResult
	| GroupsSearchCountResult

export interface GroupsSearchResponse<R = GroupsSearchResultType> {
	results: R[]
	nextCursorId?: string
	objectsCount: number
	totalCount: number
}

export interface SearchGroupMembersParams {
	model: string
	groupId: string
}

export interface SearchGroupMembersResponse<
	RelationShipSchema = unknown,
	TPermissions = ActualSearchGroupMemberPermissionsType
> {
	results: Array<{
		UID: string
		groupId: string
		model: string
		relationshipData: RelationShipSchema
		profile: {
			lastName: string
			email: string
			firstName: string
		}
		memberSince: string
		lastUpdated: string
		memberSinceTimestamp: number
		lastUpdatedTimestamp: number
		permissions?: TPermissions
	}>
}

type ActualSearchGroupMemberPermissionsType = string
type MappedSearchGroupMemberPermissionsType = GroupMemberPermission[]

export interface GroupsCreateModelParams {
	/** A name for the new model you are creating. */
	model: string
}

export interface GroupsSetSiteConfigParams {
	/** The maximum number of groups any unique user can be a member of. The default is 15. */
	maxGroupsPerUser: number
}

export class Groups {
	constructor(protected gigya: Gigya) {}

	/**
	 * This API creates a new group Model.
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/53601a5a27324294bc24203ad7e5b667.html?locale=en-US
	 */
	public createModel(params: BaseParams & GroupsCreateModelParams) {
		return this.gigya.request('accounts.groups.createModel', params)
	}

	/**
	 * This API returns all existing models for the requested API key.
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/a34f795951b045288613b93ef951828e.html?locale=en-US
	 */
	public getAllModels(params: BaseParams) {
		return this.gigya.request<GroupsGetAllModelsResponse>('accounts.groups.getAllModels', params)
	}

	/**
	 * This API deletes a schema field for an existing model.
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/31ca3dbe74c5476388bb473ba0145b2a.html?locale=en-US
	 */
	public deleteSchemaField(params: BaseParams & GroupsDeleteSchemaFieldParams) {
		return this.gigya.request('accounts.groups.deleteSchemaField', params)
	}

	/**
	 * This API returns the schema of an existing Groups model.
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/89084df3a1894a5bbb8992214e097ba0.html?locale=en-US
	 */
	public getSchema<SchemaType = any>(params: BaseParams & GroupsGetSchemaParams) {
		return this.gigya.request<GroupsGetSchemaResponse<SchemaType>>(
			'accounts.groups.getSchema',
			params
		)
	}

	/**
	 * This API defines a new schema for a model or can update an existing model schema.
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/c3c302dad22649b68f99537b7c7f0425.html?locale=en-US
	 */
	public setSchema(params: BaseParams & GroupsSetSchemaParams) {
		return this.gigya.request('accounts.groups.setSchema', params)
	}

	/**
	 * This API deletes an existing Group by ID.
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/293ab917d6f847b6b00060403e3c5f6c.html?locale=en-US
	 */
	public deleteGroup(params: BaseParams & GroupsDeleteGroupParams) {
		return this.gigya.request('accounts.groups.deleteGroup', params)
	}

	/**
	 * This API returns the information for an existing Group.
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/ee84d6036b3f4e27ba7b89cd191fa206.html?locale=en-US
	 */
	public getGroupInfo<GroupInfoType>(params: BaseParams & GroupsGetGroupInfoParams) {
		return this.gigya.request<GroupsGetGroupInfoResponse<GroupInfoType>>(
			'accounts.groups.getGroupInfo',
			params
		)
	}

	/**
	 * This API creates a new Group.
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/d7234164b8ed4dc4aeca8e24f02ba97a.html?locale=en-US
	 */
	public registerGroup<GroupDataType>(
		params: BaseParams & GroupsRegisterGroupParams<GroupDataType>
	) {
		return this.gigya.request('accounts.groups.registerGroup', params)
	}

	/**
	 * This API updates an existing Group's information.
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/a92c77976de04fb9ba8de9c024f1d041.html?locale=en-US
	 */
	public setGroupInfo<GroupDataType>(params: BaseParams & GroupsSetGroupInfoParams<GroupDataType>) {
		return this.gigya.request('accounts.groups.setGroupInfo', params)
	}

	public assignGroupMember<RelationshipDataType>(
		params: BaseParams & GroupsAssignGroupMemberParams<RelationshipDataType>
	) {
		if (Array.isArray(params.permissions)) {
			params.permissions = params.permissions.join(',')
		}
		return this.gigya.request('accounts.groups.assignGroupMember', params)
	}

	public removeMember(params: BaseParams & GroupsRemoveMemberParams) {
		return this.gigya.request('accounts.groups.removeMember', params)
	}

	public setGroupMemberInfo<RelationshipDataType>(
		params: BaseParams & GroupsSetGroupMemberInfoParams<RelationshipDataType>
	) {
		if (Array.isArray(params.permissions)) {
			params.permissions = params.permissions.join(',')
		}
		return this.gigya.request('accounts.groups.setGroupMemberInfo', params)
	}

	/**
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/082d770880254d9ca4b3c49920e77e17.html?locale=en-US
	 */
	public getSiteConfig() {
		return this.gigya.request<GroupsGetSiteConfigResponse>('accounts.groups.getSiteConfig')
	}

	/**
	 * This API sets the maximum number of groups a user can belong to.
	 * This value is enforced at the site group level, a child site can not override this value.
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/5b1d60cfee1746cf8e19ca1704b33850.html?locale=en-US
	 */
	public setSiteConfig(params: BaseParams & GroupsSetSiteConfigParams) {
		return this.gigya.request('accounts.groups.setSiteConfig', params)
	}

	/**
	 *
	 * @see https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/522c165b6dd34cbe856e62ffd0405f24.html?locale=en-US
	 */
	public search<R = GroupsSearchResponse>(params: BaseParams & GroupsSearchParams) {
		return this.gigya.request<R>('accounts.groups.search', params)
	}

	public async searchGroupMembers<TRelationShipData>(
		params: BaseParams & SearchGroupMembersParams
	) {
		const actual = await this.gigya.request<SearchGroupMembersResponse<TRelationShipData>>(
			'accounts.groups.searchGroupMembers'
		)
		const mapped: SearchGroupMembersResponse<
			TRelationShipData,
			MappedSearchGroupMemberPermissionsType
		> = {
			...actual,
			results: actual.results.map((r) => {
				const { permissions, ...rest } = r
				return {
					...rest,
					// if permissions are in the results, split them to end up with our typed array
					...(permissions && {
						permissions: permissions.split(',') as MappedSearchGroupMemberPermissionsType
					})
				}
			})
		}
		return mapped
	}
}

export interface Model {
	model: string
}

interface GroupsGetAllModelsParams {}

interface GroupsGetAllModelsResponse {
	models: Model[]
}

export default Groups
